import { AuthStore } from './AuthStore';
import { ordersApi } from '@/api/orders';
import { CartStore } from './CartStore';
import { OrderStore } from './OrderStore';
import { ProductStore } from './ProductStore';
import { RealtimeStore } from './RealtimeStore';
import { SessionStore } from './SessionStore';
import { TableStore } from './TableStore';
import { applyTheme, type ThemeStore } from '@/theme';
import { hasFeature, isRetailMode } from '@/lib/business-profile';
/*
  Deep import, NOT the `@/features/pos/queue` barrel.

  RootStore is constructed by AppProviders, so everything it can reach
  statically lands in the entry chunk a cashier downloads before the register
  paints. The barrel re-exports QueuePage, which reaches the order drawer, the
  whole `components/pos` set and through it every `components/ui` primitive —
  so asking for one 40-line notifier dragged the board, vaul and react-hook-form
  into the eager payload and pushed bundle:check past its 300 KB budget. The
  chime module itself has no UI.
*/
import { createOrderNotifier } from '@/features/pos/queue/chime';
import { StoreSetupStore } from '@/features/office/online-store/store-setup-store';

/**
 * RootStore — owned by the integration lead only (11-v1-agent-tasks.md §2).
 *
 * Feature agents implement store classes inside their owned paths; the lead
 * registers each store here AFTER the task packet is accepted. Every
 * server-backed store follows the MobX server-state discipline in
 * 01-architecture.md §4 (scope keys, status enum, stale guards, explicit
 * invalidation, zod-parsed boundaries).
 */
export class RootStore {
  /** V1-02 — token/user/scope; self-attaches to the axios clients. */
  auth: AuthStore;
  /** V1-01 — light/dark + runtime org primary (applies tokens on boot). */
  theme: ThemeStore;
  /** V1-06 — day-session (shift) state keyed by terminal scope. */
  session: SessionStore;
  /** V1-04 — register catalog + cart/checkout. */
  products: ProductStore;
  cart: CartStore;
  /** V1-05 — order queue/history/detail + realtime reconciliation. */
  orders: OrderStore;
  /** V1 fast-follow — restaurant floor and the tab open on each table. */
  tables: TableStore;
  realtime: RealtimeStore;
  /** Ecommerce activation guide and live storefront readiness, keyed by organization. */
  storeSetup: StoreSetupStore;

  constructor() {
    this.auth = new AuthStore();
    // Boot entry: returns the shared ThemeStore (shells default to the same
    // instance) and applies persisted dark class + default primary ramp.
    this.theme = applyTheme();
    this.session = new SessionStore(this.auth);
    this.products = new ProductStore({
      getScopeKey: () =>
        `${this.auth.organization?.id ?? 'none'}:${this.auth.branch?.id ?? 'none'}:${this.auth.terminal?.id ?? 'none'}`,
      // A shop lists each product once. The featured shelf duplicates a
      // product on purpose (ProductStore.menuSections), which suits a menu's
      // "popular" row and not a catalogue of distinct SKUs.
      showFeaturedShelf: () => hasFeature(this.auth.organization?.type, 'featured-shelf'),
      getMenuGrouping: () =>
        isRetailMode(this.auth.organization?.type) ? 'collection' : 'category',
    });
    this.cart = new CartStore({
      // Explicit draft identity. A recovered ticket must never cross a branch
      // or terminal switch, nor surface for a different cashier on a shared
      // counter device. CartStore has an auth-snapshot fallback for standalone
      // use; this wins.
      getScopeKey: () => {
        const userId = this.auth.user?.id;
        if (userId == null) return null;
        return [
          userId,
          this.auth.organization?.id ?? 'none',
          this.auth.branch?.id ?? 'none',
          this.auth.terminal?.id ?? 'none',
        ].join(':');
      },
    });
    this.orders = new OrderStore({
      api: { ...ordersApi, list: ordersApi.listForRegister },
    });
    this.tables = new TableStore();
    this.realtime = new RealtimeStore({
      orders: this.orders,
      // A tab IS an order, so the same two events on the same channel already
      // describe every change a table can make — no second subscription.
      tables: this.tables,
      notifier: createOrderNotifier(),
      // Office users without a terminal have no shift to open, but still need
      // website-order alerts from the application-wide realtime connection.
      isDayOpen: () => this.session.isDayOpen || this.auth.terminal === null,
    });
    this.storeSetup = new StoreSetupStore();
    // Connect/disconnect lifecycle lives in providers' RealtimeSync (reacts to
    // auth + organization); this listener only resets scoped data.
    this.auth.registerScopeListener((change) => {
      this.session.onScopeChange(change);
      this.reset();
    });
    // AccessSync refreshes access after React commits this store and attaches
    // it to the API. Constructor requests would also run for the extra store
    // StrictMode creates and discards during development.
  }

  /** Resets every scoped store on scope change or logout (auth itself excluded). */
  reset(): void {
    this.products.reset();
    this.cart.reset();
    this.orders.reset();
    this.tables.reset();
    this.storeSetup.reset();
  }
}
